import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [tickets, setTickets] = useState([]);
  const [types, setTypes] = useState(null);
  const [selected, setSelected] = useState('0');

  const [ticketCount, setTicketCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [feesTotal, setFeesTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [amount, setAmount] = useState(0);

  const [event, setEvent] = useState(null);
  const [account, setAccount] = useState(null);

  const [isPrinting, setIsPrinting] = useState(false);

  // Value to check if reserved_seat_prefix is to be used
  const [reservedSeatSection, setReservedSeatSection] = useState(false);
  const [reservedSeatPrefix, setReservedSeatPrefix] = useState('');

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedTickets = localStorage.getItem('tickets');
    if (savedTickets) {
      setTickets(JSON.parse(savedTickets));

      // Set the selected ticket to the first ticket in the list
      setSelected(Object.keys(savedTickets)[0]);
    }
    // Load other state variables here...
  }, []);

  // Save data to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('tickets', JSON.stringify(tickets));
  }, [tickets]);

  // Recalculates the total count of tickets on tickets change
  useEffect(() => {
    const count = Object.keys(tickets).reduce((acc, key) => {
      const ticketCount = tickets[key].tickets.reduce((acc, tick) => {
        return acc + (tick.count || 0);
      }, 0);
      return acc + ticketCount;
    }, 0);
    setTicketCount(count);
  }, [tickets]);

  const [selectedSeatObject, setSelectedSeatObject] = useState({});

  const [seatId, setSeatId] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);

  const [seatsModalIsVisible, setSeatsModalIsVisible] = useState(false);

  const [connectionStatus, setConnectionStatus] = useState('');
  const [currentStatus, setCurrentStatus] = useState('Printing...');

  const [bestTickets, setBestTickets] = useState({});
  // Create a set to store the best available seats from the API
  const [bestSeats, setBestSeats] = useState(new Set());
  const [selectedSeats, setSelectedSeats] = useState({});

  // Reference for the chart
  const chartRef = useRef(null);

  // Reference for printer
  const printer = useRef();

  // Add a new ticket
  const addTicket = (ticket) => {
    setTicketCount((count) => count + 1);

    setTickets((tickets) => {
      const newTickets = tickets[selected].tickets?.reduce((acc, tick) => {
        if (ticket.id === tick.id) {
          // If the ticket is with reserved seat, add the seat to the seats array
          if (tick.reserved_seat) {
            return [
              ...acc,
              {
                ...tick,
                count: tick.count + 1,
                seats:
                  tick.seats && tick.seats.length > 0
                    ? [...tick.seats, ticket.seat]
                    : [ticket.seat],
              },
            ];
          }
          return [...acc, { ...tick, count: tick.count + 1 }];
        } else return [...acc, { ...tick }];
      }, []);

      return {
        ...tickets,
        [selected]: { ...tickets[selected], tickets: newTickets },
      };
    });
  };

  const removeTicket = (ticket) => {
    setTicketCount((count) => (count ? count - 1 : 0));

    setTickets((tickets) => {
      const newTickets = tickets[selected]?.tickets?.reduce((acc, tick) => {
        // If the ticket is with reserved seat, remove the seat from the seats array
        if (ticket.id === tick.id && tick.reserved_seat) {
          const newSeats = tick?.seats?.filter(
            (seat) => seat.id !== ticket.seat.id
          );

          return [
            ...acc,
            {
              ...tick,
              count: tick.count ? tick.count - 1 : 0,
              seats: newSeats,
            },
          ];
        } else if (ticket.id === tick.id)
          return [...acc, { ...tick, count: tick.count ? tick.count - 1 : 0 }];
        else return [...acc, { ...tick }];
      }, []);

      return {
        ...tickets,
        [selected]: { ...tickets[selected], tickets: newTickets },
      };
    });
  };

  // Find and update the seat of a selected ticket based on a seat that is passed in
  const updateTicketSeat = (seat) => {
    setTickets((tickets) => {
      const newTickets = tickets[selected]?.tickets?.reduce((acc, tick) => {
        // If the ticket is with reserved seat, update the seat in the seats array
        if (tick.reserved_seat && tick.seats) {
          const newSeats = tick.seats.map((s) => {
            if (s.id === seat.id) {
              return { ...s, ...seat };
            }
            return s;
          });

          return [
            ...acc,
            {
              ...tick,
              seats: newSeats,
            },
          ];
        } else {
          // If the ticket is not the one we are looking for, return it as is
          return [...acc, { ...tick }];
        }
      }, []);

      return {
        ...tickets,
        [selected]: { ...tickets[selected], tickets: newTickets },
      };
    });
  };

  // Add the ticket on seat selection
  const addTicketSeat = (seat, seat_ids) => {
    const ticket = tickets[selected]?.tickets.find(
      (tick) =>
        tick.reserved_seat_category === seat.category.label &&
        tick.seat_ids === seat_ids
    );

    const seatIdKey = seat_ids.join('-');

    // Check if the ticket already has the seat
    const existingTicket = tickets[selected]?.tickets.find(
      (tick) =>
        tick.reserved_seat_category === seat.category.label &&
        tick.seat_ids === seat_ids &&
        tick.seats?.some((s) => s.id === seat.id)
    );

    // If the ticket already has the seat, do not add it again
    if (existingTicket) {
      return;
    }

    addTicket({
      ...ticket,
      seat: seat,
      price: seat?.price ?? ticket?.track_price,
    });

    setSelectedSeatObject((prevSeats) => {
      const updatedSeats = { ...prevSeats };
      const sectionKey = ticket?.reserved_seat_section
        ? seat?.labels?.section
        : ticket?.reserved_seat_prefix;

      if (!sectionKey) return updatedSeats;

      setReservedSeatPrefix(ticket?.reserved_seat_prefix);
      setReservedSeatSection(!!ticket?.reserved_seat_section);

      // Ensure the base structure exists
      if (!updatedSeats[seatIdKey]) {
        updatedSeats[seatIdKey] = {};
      }

      const existingSeats = updatedSeats[seatIdKey][sectionKey] || [];

      const seatExists = existingSeats.some(
        (prevSeat) => prevSeat.id === seat.id
      );

      if (!seatExists) {
        updatedSeats[seatIdKey][sectionKey] = [
          ...existingSeats,
          { ...seat, ticket: { ...ticket, count: 1 } },
        ];
      }

      return updatedSeats;
    });
  };

  // Remove the ticket on seat deselection
  const removeTicketSeat = (seat, seat_ids) => {
    const ticket = tickets[selected]?.tickets.find(
      (tick) =>
        tick.reserved_seat_category === seat.category.label &&
        tick.seat_ids === seat_ids
    );

    const seatIdKey = seat_ids.join('-');

    removeTicket({ ...ticket, seat: seat });

    setSelectedSeatObject((seats) => {
      // Check if reserved_seat_prefix is true to determine whether to use the section or reserved_seat_section
      if (ticket?.reserved_seat_prefix) {
        //Check if the category already exists in the object
        if (seats[seatIdKey][seat?.labels.section]) {
          //If it exists, remove the seat from the array
          seats[seatIdKey][seat?.labels.section] = seats[seatIdKey][
            seat?.labels.section
          ].filter((prevSeat) => prevSeat.id !== seat.id);

          //check if the category is empty, if it is, remove it from the object
          if (seats[seatIdKey][seat?.labels.section].length === 0) {
            delete seats[seatIdKey][seat?.labels.section];
          }
        }
      } else {
        // Use the ticket reserved_seat_section if reserved_seat_prefix is false

        //Check if the category already exists in the object
        if (ticket?.reserved_seat_prefix) {
          //If it exists, remove the seat from the array
          seats[seatIdKey][ticket?.reserved_seat_prefix] = seats[seatIdKey][
            ticket?.reserved_seat_prefix
          ].filter((prevSeat) => prevSeat.id !== seat.id);

          //check if the category is empty, if it is, remove it from the object
          if (seats[seatIdKey][ticket?.reserved_seat_prefix].length === 0) {
            delete seats[seatIdKey][ticket?.reserved_seat_prefix];
          }
        }
      }

      return seats;
    });
  };

  const removeAllTicket = (ticket) => {
    const newTickets = Object.keys(tickets).reduce((acc, key) => {
      const newTickets = tickets[key].tickets.map((tick) => {
        if (ticket.id === tick.id && !tick.reserved_seat) {
          setTicketCount((count) => count - tick.count);

          return { ...tick, count: 0 };
        } else return { ...tick };
      });

      return [...acc, { ...tickets[key], tickets: newTickets }];
    }, []);

    setTickets(newTickets);
  };

  const clearTicket = async (isBestAvailable = false) => {
    const newTickets = Object.keys(tickets).reduce((acc, key) => {
      const newTickets = tickets[key].tickets.map((tick) => {
        return { ...tick, count: 0, seats: [] };
      });

      return [...acc, { ...tickets[key], tickets: newTickets }];
    }, []);

    setTickets(newTickets);
    setTicketCount(0);
    setTotal(0);
    setSubtotal(0);
    setFeesTotal(0);
    setDiscount(0);
    setAmount(0);

    if (isBestAvailable) {
      // Reset the selected seats
      setSelectedSeatObject({});
    }

    if (!isBestAvailable) {
      await resetSeats();

      // Reset the selected ticket to the first ticket in the list
      setSelected(Object.keys(newTickets)[0]);
    }
  };

  const clearBestTickets = async (seat_ids) => {
    const newTickets = Object.keys(tickets).reduce((acc, key) => {
      const newTickets = tickets[key].tickets.map((tick) => {
        if (tick?.seat_ids === seat_ids) {
          return { ...tick, count: 0, seats: [] };
        } else return { ...tick };
      });

      return [...acc, { ...tickets[key], tickets: newTickets }];
    }, []);

    setTickets(newTickets);
    setSelectedSeatObject((seats) => {
      const seatIdKey = seat_ids.join('-');
      // Remove the selected seats from the selectedSeatObject
      delete seats[seatIdKey];
      return seats;
    });
  };

  const resetSeats = async () => {
    setBestTickets({});
    setBestSeats(new Set());
    setSelectedSeats({});

    // Delete the hold token from sessionStorage
    sessionStorage.removeItem('seatsio');

    // Add a delay before closing the modal
    setTimeout(() => {
      setBestSeats(new Set());
      setSelectedSeatObject({});
    }, 500);
  };

  return (
    <CartContext.Provider
      value={{
        tickets,
        addTicket,
        setTickets,
        removeTicket,
        ticketCount,
        setTicketCount,
        total,
        setTotal,
        amount,
        setAmount,
        feesTotal,
        setFeesTotal,
        clearTicket,
        removeAllTicket,
        types,
        setTypes,
        discount,
        setDiscount,
        subtotal,
        setSubtotal,
        selectedSeatObject,
        setSelectedSeatObject,
        seatId,
        setSeatId,
        addTicketSeat,
        removeTicketSeat,
        chartRef,
        resetSeats,
        seatsModalIsVisible,
        setSeatsModalIsVisible,
        accountId,
        setAccountId,
        purchaseId,
        setPurchaseId,
        selected,
        setSelected,
        connectionStatus,
        setConnectionStatus,
        printer,
        event,
        setEvent,
        isPrinting,
        setIsPrinting,
        account,
        setAccount,
        reservedSeatPrefix,
        reservedSeatSection,
        bestTickets,
        setBestTickets,
        bestSeats,
        setBestSeats,
        clearBestTickets,
        selectedSeats,
        setSelectedSeats,
        currentStatus,
        setCurrentStatus,
        updateTicketSeat,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('UseCart must be used within a CartProvider');
  }
  return context;
};
